// src/pages/DJQrPage.tsx
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import QRCode from "react-qr-code";
import Cropper, { Area } from "react-easy-crop";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { CapacitorPassToWallet } from "capacitor-pass-to-wallet";
import { usersApi } from "../services/users";
import { walletApi } from "../services/wallet";
import type { User } from "../types/User";
import "./DJQrPage.css";
import { pageCache } from "../lib/pageCache";
import "../components/PaintSkeleton.css";
import { FaLink, FaDownload, FaImage, FaTimes, FaPalette } from "react-icons/fa";
import {
  buildPassDesign,
  qrSvgToPngDataUrl,
  WALLET_PASS_COLOR_OPTIONS,
  WalletPassColor,
} from "../utils/walletPassRenderer";
import { pickAvatarPhotoNative } from "../lib/avatarPhoto";
import { cropImageToDataUrl } from "../utils/cropImage";
import QrGateCard from "../components/QrGateCard";
import GoogleWalletButton, { isAndroid } from "../components/GoogleWalletButton";
import AppleWalletBadge from "../components/AppleWalletBadge";
import WalletLastStep from "../components/WalletLastStep";
import type { IgVerifyStatus } from "../lib/instagramLogin";
import { t } from "../i18n";
import { T } from "../i18n/T";
import { SpotlightSheet } from "../components/SpotlightSettings";
import { SpotlightIcon } from "../components/spotlightIcons";
import { useSpotlightSettings } from "../hooks/useSpotlightSettings";
import { AuthContext } from "../context/AuthContext";
import { hintDue, endHint } from "../lib/welcomeHints";
import "./DJQrActions.css";

const APP_BASE_URL = "https://requesta.app";

type Profile = User;

// The brand R in the middle of the pass, loaded ONCE per app session (1.5.2
// TestFlight: reloading it on every visit drew the pass without the R and
// then again with it — a half-second glitch on each return to this tab).
// `failed` lets the pass draw without it rather than never.
const rLogo: { img: HTMLImageElement | null; failed: boolean; waiters: ((img: HTMLImageElement | null) => void)[] } = {
  img: null,
  failed: false,
  waiters: [],
};
function loadRLogo(done: (img: HTMLImageElement | null) => void) {
  if (rLogo.img || rLogo.failed) return done(rLogo.img);
  rLogo.waiters.push(done);
  if (rLogo.waiters.length > 1) return;
  const img = new Image();
  const settle = (ok: boolean) => {
    if (ok) rLogo.img = img;
    else rLogo.failed = true;
    rLogo.waiters.splice(0).forEach((w) => w(rLogo.img));
  };
  img.onload = () => settle(true);
  img.onerror = () => settle(false);
  img.src = "/r.png";
}

const isIOS = () => {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1);
};

const DJQrPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  // How the Instagram round trip ended, if they just came back from one
  // (pages/InstagramCallbackPage.tsx lands them here with ?igVerify=…).
  const [searchParams, setSearchParams] = useSearchParams();
  const igStatus = (searchParams.get("igVerify") || null) as IgVerifyStatus | null;
  // A brand-new talent arrives once with ?wallet=1 (lib/walletMoment): the
  // full-screen Apple Wallet step, then — after "Later" — a ring + tip
  // around the button on this page until they tap it or leave.
  const [walletStep, setWalletStep] = useState<"full" | "spot" | null>(() =>
    searchParams.get("wallet") === "1" && isIOS() ? "full" : null
  );
  const [passUrl, setPassUrl] = useState<string | null>(null);
  // Fan's Spotlight: the fourth round button, on the talent's OWN QR page only (its settings are the signed-in
  // talent's), shown once the server has answered (an older server: no button).
  const { user } = useContext(AuthContext);
  const own = !!user?.slug && user.slug === slug;
  const spotlight = useSpotlightSettings(own);
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  // A small "New" on the Spotlight button until the talent's first tap on it
  // (founder, 1.5.2 b50), per account, per device (lib/welcomeHints).
  const [spotlightNew, setSpotlightNew] = useState(() => hintDue("spotlight", user?.slug));
  const openSpotlight = () => {
    if (user?.slug) endHint("spotlight", user.slug);
    setSpotlightNew(false);
    setSpotlightOpen(true);
  };
  // The param can also arrive while this page is already open (the talent
  // was on their QR tab when the step fired): no remount then, so react when
  // it APPEARS — the one-time flag is already spent (2026-10-10).
  useEffect(() => {
    if (searchParams.get("wallet") !== "1") return;
    if (isIOS()) setWalletStep("full");
    const next = new URLSearchParams(searchParams);
    next.delete("wallet");
    setSearchParams(next, { replace: true }); // a refresh never re-opens it
  }, [searchParams, setSearchParams]);
  // Seed from the session cache so tab revisits render instantly instead of
  // flashing "Loading..." — the fetch below still runs and refreshes silently.
  const cachedProfile = slug ? pageCache.get<Profile>(`qr-profile:${slug}`) : undefined;
  const [profile, setProfile] = useState<Profile | null>(cachedProfile ?? null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(
    (slug && pageCache.get<string>(`qr-png:${slug}`)) || null
  );
  // The last drawing of the pass, shown on the very first frame of a later
  // visit (the canvas draws a moment after mount); a skeleton card on the
  // first-ever open — never a "Loading…" line (1.5.2).
  const [still, setStill] = useState<string | null>((slug && pageCache.get<string>(`qr-still:${slug}`)) || null);
  const [drawn, setDrawn] = useState(false);
  const [rLogoImg, setRLogoImg] = useState<HTMLImageElement | null>(rLogo.img);
  const [rLogoSettled, setRLogoSettled] = useState(!!rLogo.img || rLogo.failed);
  const [color, setColor] = useState<WalletPassColor>(cachedProfile?.walletPass?.color || "green");
  const [customColor, setCustomColor] = useState<string | null>(
    cachedProfile?.walletPass?.customColor ?? null
  );
  const [customLogoDataUrl, setCustomLogoDataUrl] = useState<string | null>(null);
  const [savedImageUrl, setSavedImageUrl] = useState<string | null>(null);

  // Cropper state — when an image is picked, we open the cropper modal and
  // hold the source URL + the user's current crop selection until they confirm.
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const [cropZoom, setCropZoom] = useState(1);
  const [cropAreaPixels, setCropAreaPixels] = useState<Area | null>(null);

  const [savingColor, setSavingColor] = useState(false);
  const [downloadingWallet, setDownloadingWallet] = useState(false);

  const svgWrapperRef = useRef<HTMLDivElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const colorInputRef = useRef<HTMLInputElement | null>(null);
  const logoInputRef = useRef<HTMLInputElement | null>(null);

  const djUrl = useMemo(() => `${APP_BASE_URL}/dj/${slug}/request`, [slug]);

  // FREE TIER: the QR is live from day one — a talent with no bank, not yet
  // approved, or DECLINED takes free requests with it (founder, 1.5.1: a
  // declined talent keeps their free page and QR). Declined is the one state
  // with a card here: a plain note above the real code (components/QrGateCard).
  const declined = profile?.approvalStatus === "declined";

  // The brand R (loaded once per app session — see loadRLogo).
  useEffect(() => {
    let live = true;
    loadRLogo((img) => {
      if (!live) return;
      setRLogoImg(img);
      setRLogoSettled(true);
    });
    // Safety net: a slow first load never keeps the pass undrawn (it redraws
    // with the R the moment it arrives).
    const fallback = setTimeout(() => live && setRLogoSettled(true), 1500);
    return () => {
      live = false;
      clearTimeout(fallback);
    };
  }, []);

  // Fetch profile + saved color. Re-runs when the slug changes, when this tab
  // regains focus, or when the page becomes visible again — so a name change
  // made in /profile (or another tab) is reflected here without a manual reload.
  useEffect(() => {
    if (!slug) return;

    let cancelled = false;
    const load = () => {
      usersApi
        .get(slug)
        .then((res) => {
          if (cancelled) return;
          pageCache.set(`qr-profile:${slug}`, res);
          setProfile(res);
          if (res.walletPass?.color) setColor(res.walletPass.color);
          if (res.walletPass?.customColor) setCustomColor(res.walletPass.customColor);
        })
        .catch((err) => console.error("Error fetching DJ profile:", err));
    };

    load();

    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", load);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", load);
    };
  }, [slug]);

  // Convert the hidden QR SVG to a high-res PNG once the profile is loaded
  useEffect(() => {
    if (!profile) return;
    const timer = setTimeout(async () => {
      const svgEl = svgWrapperRef.current?.querySelector("svg");
      if (!svgEl) return;
      const dataUrl = await qrSvgToPngDataUrl(svgEl as SVGSVGElement);
      if (slug) pageCache.set(`qr-png:${slug}`, dataUrl);
      setQrDataUrl(dataUrl);
    }, 100);
    return () => clearTimeout(timer);
    // Keyed on the loaded profile; slug only names the cache entry and always
    // matches the profile fetched for it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // Re-render the preview whenever color, profile, QR, or logo changes — and
  // when the Wallet step opens, which gets a copy of the drawing (it can open
  // on a page that has already drawn; 2026-10-10).
  useEffect(() => {
    if (!profile || !qrDataUrl || !previewCanvasRef.current) return;
    // Never draw the pass without its R and then again with it: wait for the
    // R (unless a custom logo replaces it). The remembered drawing shows meanwhile.
    if (!customLogoDataUrl && !rLogoSettled) return;
    let cancelled = false;
    (async () => {
      const designCanvas = await buildPassDesign({
        color,
        customColor,
        djName: profile.name,
        qrImage: qrDataUrl,
        rLogo: rLogoImg,
        customCenterLogo: customLogoDataUrl,
        width: 1200,
      });
      if (cancelled) return;
      if (walletStep === "full") setPassUrl(designCanvas.toDataURL("image/png"));
      const target = previewCanvasRef.current;
      if (!target) return;
      target.width = designCanvas.width;
      target.height = designCanvas.height;
      const ctx = target.getContext("2d")!;
      ctx.drawImage(designCanvas, 0, 0);
      setDrawn(true);
      try {
        const png = designCanvas.toDataURL("image/png");
        if (slug) pageCache.set(`qr-still:${slug}`, png);
        setStill(png);
      } catch {
        /* a tainted canvas can't be read back — the next visit just draws */
      }
    })();
    return () => {
      cancelled = true;
    };
    // slug only names the cache entry (always the profile's own).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, qrDataUrl, color, customColor, rLogoImg, rLogoSettled, customLogoDataUrl, walletStep]);

  const persistColor = async (next: WalletPassColor, nextCustom: string | null) => {
    if (!slug) return;
    setSavingColor(true);
    try {
      await usersApi.setWalletPass(slug, { color: next, customColor: nextCustom });
    } catch (err) {
      console.error("Failed to save wallet pass color:", err);
    } finally {
      setSavingColor(false);
    }
  };

  // The colour just picked is also what the NEXT visit starts from (1.5.2
  // TestFlight: the remembered profile still held the old colour, so a return
  // to this tab drew the old colour for a moment before the server answered).
  const rememberColor = (next: WalletPassColor, nextCustom: string | null) => {
    if (!slug) return;
    const cached = pageCache.get<Profile>(`qr-profile:${slug}`);
    if (cached) pageCache.set(`qr-profile:${slug}`, { ...cached, walletPass: { ...cached.walletPass, color: next, customColor: nextCustom } });
  };

  const handlePresetPick = (next: WalletPassColor) => {
    // Before the profile (and its saved colour) is in, a pick has nothing to replace.
    if (!profile) return;
    if (next === color && !customColor) return;
    setColor(next);
    setCustomColor(null);
    rememberColor(next, null);
    persistColor(next, null);
  };

  const handleCustomColorChange = (hex: string) => {
    if (!profile || !/^#[0-9a-fA-F]{6}$/.test(hex)) return;
    setColor("custom");
    setCustomColor(hex);
    rememberColor("custom", hex);
    persistColor("custom", hex);
  };

  // In the app, skip the OS file sheet (it offers Drive/Files clutter) and
  // use the native two-option picker, same as the profile photo (founder
  // decision). Web keeps the plain input — Safari owns that menu.
  const handleLogoClick = async () => {
    if (!Capacitor.isNativePlatform()) {
      logoInputRef.current?.click();
      return;
    }
    const result = await pickAvatarPhotoNative();
    if (result.status === "ok") {
      setCropSrc(result.dataUrl);
      setCropPosition({ x: 0, y: 0 });
      setCropZoom(1);
      setCropAreaPixels(null);
    } else if (result.status === "denied") {
      window.alert(t("dJQrPage.photoAccessIsOff"));
    } else if (result.status === "error") {
      window.alert(t("dJQrPage.couldnTOpenYour"));
    }
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (ev.target?.result) {
        // Open the cropper instead of setting the QR-center logo directly,
        // so users can frame their image into a square (no more squashed logos).
        setCropSrc(ev.target.result as string);
        setCropPosition({ x: 0, y: 0 });
        setCropZoom(1);
        setCropAreaPixels(null);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCropAreaPixels(areaPixels);
  }, []);

  const cancelCrop = () => setCropSrc(null);

  const confirmCrop = async () => {
    if (!cropSrc || !cropAreaPixels) return;
    try {
      const cropped = await cropImageToDataUrl(cropSrc, cropAreaPixels);
      setCustomLogoDataUrl(cropped);
    } catch (err) {
      console.error("Failed to crop image:", err);
    } finally {
      setCropSrc(null);
    }
  };

  const handleSaveToCameraRoll = async () => {
    if (!profile || !qrDataUrl) return;
    const canvas = await buildPassDesign({
      color,
      customColor,
      djName: profile.name,
      qrImage: qrDataUrl,
      rLogo: rLogoImg,
      customCenterLogo: customLogoDataUrl,
      width: 1500,
    });

    const filename = `request-${profile.slug}.png`;

    // Try Web Share API on HTTPS first — gives "Save Image" → Camera Roll natively.
    if (window.isSecureContext && typeof navigator.canShare === "function") {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/png")
      );
      if (blob) {
        const file = new File([blob], filename, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], title: t("dJQrPage.myQrCode") });
            return;
          } catch (err: any) {
            if (err.name === "AbortError") return;
            console.error("Share failed:", err);
          }
        }
      }
    }

    // iOS fallback: open the image in a new tab. iOS Safari natively shows a
    // standalone image page where long-press → "Save to Photos" works
    // reliably, even on plain HTTP. (The in-page modal didn't always trigger
    // the iOS image action sheet.)
    if (isIOS()) {
      const dataUrl = canvas.toDataURL("image/png");
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${
        profile.name
      } — Request App QR</title><meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5"><style>html,body{margin:0;padding:0;background:#000;color:#fff;font-family:-apple-system,BlinkMacSystemFont,sans-serif}body{padding:24px 16px;text-align:center;min-height:100vh;box-sizing:border-box}p{margin:0 0 16px;font-size:15px;line-height:1.4;opacity:.85}img{display:block;max-width:100%;height:auto;border-radius:12px;margin:0 auto;-webkit-touch-callout:default;-webkit-user-select:auto;user-select:auto}</style></head><body><p>Press &amp; hold the image,<br>then tap <strong>"Save to Photos"</strong></p><img src="${dataUrl}" alt="QR"></body></html>`;
      const newWin = window.open("", "_blank");
      if (newWin) {
        newWin.document.write(html);
        newWin.document.close();
        return;
      }
      // Popup blocked — fall back to the in-page modal.
      setSavedImageUrl(dataUrl);
      return;
    }

    // Desktop fallback: download
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, "image/png");
  };

  const closeSavedImageModal = () => setSavedImageUrl(null);

  const handleAddToAppleWallet = async () => {
    if (!slug || !profile) return;
    setDownloadingWallet(true);
    try {
      if (Capacitor.isNativePlatform()) {
        // Native iOS can't do the blob-download path below: WKWebView has no
        // download manager and CapacitorHttp mangles binary blobs, so the
        // anchor-click silently does nothing.
        //
        // Primary (zero-flicker): fetch the pass as base64 JSON (survives
        // CapacitorHttp) and hand it straight to Apple's native Add-to-Wallet
        // sheet via PKAddPassesViewController.
        //
        // Fallback (device-verified in 1.1(8)/(9)): short-lived
        // self-authenticating link opened in Safari (SFSafariViewController),
        // where iOS shows the same sheet after a brief Safari flicker. Safari
        // doesn't carry the app login, which is why that link authenticates
        // itself via a signed token in the URL.
        try {
          const data = await walletApi.passData();
          await CapacitorPassToWallet.addToWallet({ base64: data.base64 });
        } catch (nativeErr: any) {
          // Plugin code 100 = the pass is already in Wallet. That's success
          // from the DJ's point of view — say so instead of re-adding.
          if (/already added/i.test(nativeErr?.message || "")) {
            alert(t("dJQrPage.thisPassIsAlready"));
          } else {
            console.error("Native Add-to-Wallet failed, falling back to Safari:", nativeErr);
            const data = await walletApi.passLink();
            await Browser.open({ url: data.url, presentationStyle: "popover" });
          }
        }
      } else {
        // Web (requesta.app): download the .pkpass as a blob. The pass is built
        // entirely server-side from native Apple text fields — no rendered
        // images are sent; just request the pass. Unchanged.
        const res = await walletApi.generatePass();
        const blob = new Blob([res], { type: "application/vnd.apple.pkpass" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `request-${slug}.pkpass`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (err) {
      console.error("Failed to open Apple Wallet pass:", err);
      alert(t("dJQrPage.couldnTOpenYour2"));
    } finally {
      setDownloadingWallet(false);
      setWalletStep(null);
    }
  };

  // No Stripe gate here anymore. Login auto-converts un-onboarded talents to
  // the deferred-Stripe path (charges flow to platform, paid out on first
  // cashout post-onboarding) so every authenticated talent can use their QR
  // immediately. The "connect bank" nudge lives on /profile via the Pending
  // Earnings widget instead.
  return (
    <div className="qr-container">
      {/* Hidden SVG used to source the QR pixels for the renderer. Rendered
          inside a ZERO-SIZE clipped box: the old style (absolute + opacity:0,
          no size) left a real 600px-wide invisible box whose containing block
          resolved OUTSIDE the container's overflow:hidden — widening the
          document and letting iOS drag the whole page sideways (founder
          report, 1.1(26) prep; same class as the 1.1(16) swipe-pan bug).
          The renderer only serializes the SVG's markup
          (qrSvgToPngDataUrl → XMLSerializer), so layout size is irrelevant. */}
      <div
        ref={svgWrapperRef}
        aria-hidden
        style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", opacity: 0, pointerEvents: "none" }}
      >
        <QRCode value={djUrl} size={600} level="H" />
      </div>

      <h2 className="qr-title">{t("dJQrPage.myQrCode")}</h2>

      {/* Everything below the title centres in the remaining height (.qr-stack). */}
      <div className="qr-stack">

      {declined && (
        <QrGateCard
          profile={profile}
          igStatus={igStatus}
          onSaved={(patch) => {
            const next = { ...profile, ...patch };
            if (slug) pageCache.set(`qr-profile:${slug}`, next);
            setProfile(next);
          }}
        />
      )}

      <div className="pass-preview-frame">
        <canvas ref={previewCanvasRef} className="pass-preview-canvas" />
        {!drawn &&
          (still ? (
            <img src={still} alt="" aria-hidden="true" className="pass-preview-still" />
          ) : (
            <span className="psk pass-preview-skel" aria-hidden="true" />
          ))}
      </div>

      <div className="pass-color-picker">
        <p className="pass-color-label">{t("dJQrPage.chooseAColor")}</p>
        <div className="pass-color-swatches">
          {WALLET_PASS_COLOR_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              aria-label={opt.label}
              className={`pass-color-swatch ${profile && color === opt.value && !customColor ? "is-selected" : ""}`}
              style={{ background: opt.swatch }}
              onClick={() => handlePresetPick(opt.value)}
              disabled={savingColor}
            />
          ))}
          <label
            className={`pass-color-swatch pass-color-wheel ${profile && color === "custom" && customColor ? "is-selected" : ""}`}
            style={customColor ? { background: customColor } : undefined}
            aria-label={t("dJQrPage.customColor")}
          >
            {!customColor && <FaPalette />}
            <input
              ref={colorInputRef}
              type="color"
              className="pass-color-wheel-input"
              value={customColor || "#24C961"}
              onChange={(e) => handleCustomColorChange(e.target.value)}
              disabled={savingColor}
            />
          </label>
        </div>
      </div>

      {isIOS() && (
        <div className={`apple-wallet-row${walletStep === "spot" ? " apple-wallet-row--spot" : ""}`}>
          {walletStep === "spot" && <p className="apple-wallet-tip">{t("walletStep.tip")}</p>}
          <AppleWalletBadge onClick={handleAddToAppleWallet} busy={downloadingWallet} />
        </div>
      )}

      {/* Only once the server offers it: until Google grants publishing access the
          pass is demo-only and anyone else would get Google's error. */}
      {!isIOS() && isAndroid() && profile?.googleWalletEnabled && <GoogleWalletButton />}

      <div className="qr-buttons">
        <div className="qr-action">
          <Link
            to={`/dj/${slug}/request`}
            className="qr-btn"
            title={t("dJQrPage.viewMyPublicPage")}
            onClick={(e) => {
              // Native app: keep the DJ inside their app — the fan page
              // opens in the browser sheet instead of navigating this
              // webview away (founder decision).
              if (Capacitor.isNativePlatform()) {
                e.preventDefault();
                void Browser.open({ url: `${APP_BASE_URL}/dj/${slug}/request` });
              }
            }}
          >
            <FaLink />
          </Link>
          <p className="qr-action-label">{t("dJQrPage.myLink")}</p>
        </div>

        <div className="qr-action">
          <button
            onClick={handleLogoClick}
            className="qr-btn"
            title={t("dJQrPage.useACustomCenter")}
          >
            <FaImage />
          </button>
          <p className="qr-action-label">{t("dJQrPage.customLogo")}</p>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleLogoFileChange}
          />
        </div>

        {/* Not in the Android app: its WebView has no share sheet and ignores
            downloads, and no installed plugin can save a file, so the button
            did nothing (2026-10-10). Chrome on Android keeps it. */}
        {Capacitor.getPlatform() !== "android" && (
          <div className="qr-action">
            <button
              onClick={handleSaveToCameraRoll}
              className="qr-btn"
              title={t("dJQrPage.saveToCameraRoll")}
            >
              <FaDownload />
            </button>
            {/* One short word (founder, 1.5.2 b50); the full name stays on the button. */}
            <p className="qr-action-label">{t("dJQrPage.save")}</p>
          </div>
        )}

        {/* Its place is kept while the first read is on its way (1.5.2). */}
        {own && !spotlight.settings && spotlight.pending && (
          <div className="qr-action" aria-hidden="true">
            <span className="psk psk--round qr-skel-btn" />
            <p className="qr-action-label">{t("dJQrPage.spotlight")}</p>
          </div>
        )}
        {own && spotlight.settings && (
          <div className="qr-action">
            <button
              type="button"
              onClick={openSpotlight}
              className={`qr-btn qr-btn--badged${spotlight.settings.enabled ? " qr-btn--live" : ""}`}
              title={t("dJQrPage.fansSpotlight")}
              aria-label={t("dJQrPage.fansSpotlight")}
            >
              <SpotlightIcon size={24} />
              {/* On the button's corner, not beside the word: a 4-across row has
                  no room for it in every language (founder, 1.5.2 b50). Not on a
                  screen that is already on. */}
              {spotlightNew && !spotlight.settings.enabled && (
                <span className="qr-new-badge">{t("dJQrPage.new")}</span>
              )}
            </button>
            <p className="qr-action-label">{t("dJQrPage.spotlight")}</p>
          </div>
        )}
      </div>
      {own && <SpotlightSheet
        open={spotlightOpen}
        onClose={() => setSpotlightOpen(false)}
        state={spotlight}
        currency={profile?.currency}
        scanUrl={`https://www.requesta.app/${profile?.slug || slug}`}
      />}

      {customLogoDataUrl && (
        <button
          type="button"
          className="qr-remove-logo-btn"
          onClick={() => setCustomLogoDataUrl(null)}
        >
          <FaTimes size={12} style={{ marginRight: 4 }} />
          {t("dJQrPage.removeCustomLogo")}
        </button>
      )}
      </div>

      {walletStep === "full" && (
        <WalletLastStep passUrl={passUrl} busy={downloadingWallet} onAdd={handleAddToAppleWallet} onLater={() => setWalletStep("spot")} />
      )}

      {savedImageUrl && (
        <div className="save-image-modal" onClick={closeSavedImageModal}>
          <div className="save-image-card" onClick={(e) => e.stopPropagation()}>
            <p className="save-image-instructions">
              <T k="dJQrPage.pressHoldTheImage" tags={{ 0: (c) => <strong>{c}</strong> }} />
            </p>
            <img
              src={savedImageUrl}
              alt={t("dJQrPage.yourQrPressAnd")}
              className="save-image-preview"
            />
            <button
              type="button"
              className="save-image-close"
              onClick={closeSavedImageModal}
            >
              {t("dJQrPage.done")}
            </button>
          </div>
        </div>
      )}

      {cropSrc && (
        <div className="crop-modal">
          <div className="crop-modal-cropper">
            <Cropper
              image={cropSrc}
              crop={cropPosition}
              zoom={cropZoom}
              aspect={1}
              cropShape="rect"
              showGrid={false}
              onCropChange={setCropPosition}
              onZoomChange={setCropZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <div className="crop-modal-controls">
            <p className="crop-modal-hint">{t("dJQrPage.pinchToZoomDrag")}</p>
            <input
              type="range"
              className="crop-zoom-slider"
              min={1}
              max={3}
              step={0.05}
              value={cropZoom}
              onChange={(e) => setCropZoom(Number(e.target.value))}
              aria-label={t("dJQrPage.zoom")}
            />
            <div className="crop-modal-buttons">
              <button type="button" className="crop-cancel-btn" onClick={cancelCrop}>
                {t("dJQrPage.cancel")}
              </button>
              <button type="button" className="crop-confirm-btn" onClick={confirmCrop}>
                {t("dJQrPage.useThisCrop")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DJQrPage;
